<script setup>
/**
 * タイムチャート作成：チェック結果・AI 説明パネル（DEV-14983）
 *
 * - ルール照合表：ルールごとに 満たす / 未達 / 判定不可。最低・最大回数は 要求・計画・差・理由 も表示
 *   配分指定（SHARE_PREF）は 目安割合・計画の割合・あと何回 を表示
 *   （1日あたりのルールは 要求＝1日の回数、計画＝最も少ない／多い日の回数、差＝各日の差の合計）
 * - 違反一覧：クリックで該当の上映回を強調表示
 * - AI 説明：最適化方針、予測動員合計（算出不可の回数を別記）、データ根拠、全体・作品ごとの説明、未達の報告
 * 判定はすべてバックエンドの結果をそのまま表示する。
 * section で表示するブロックを切り替える（ルール照合・違反は右側、AI 説明はタイムチャートの下に置くため）
 */
import { computed } from "vue";
import { usePlanState } from "../use-plan";
import {
  RULE_CHECK_STATUS,
  RULE_KIND_OPTIONS,
  RULE_SOURCE_OPTIONS,
  isDistributorUndeterminedCheck,
  optionLabel,
  shortDateLabel,
  versionLabel,
} from "../utils";

const props = defineProps({
  // "check"：ルール照合・違反 / "summary"：AI 説明 / "all"：すべて
  section: { type: String, default: "all" },
});
const emit = defineEmits(["select-violation"]);

const showCheck = computed(() => props.section !== "summary");
const showSummary = computed(() => props.section !== "check");

// 最適化方針（summary.policy がない場合の固定文）
const POLICY_TEXT = "発行元の最低回数を優先し、その後予測動員数合計を最適化";
// 回数を比較するルールの種類
const COUNT_KINDS = ["MIN_SHOWS", "MAX_SHOWS"];

const state = usePlanState();
const {
  check, violations, ruleMap, movieMap, versionMap, screenMap, summary, estTotalSeats, estUnknownCount,
  generatedTime, validating, showsDirty,
} = state;

// ルール照合表（照合結果にルールの内容が含まれるので、ローカルのルールが未確定でも表示できる）
const ruleChecks = computed(() => (check.value?.ruleChecks || []).map((c, i) => {
  const rule = ruleMap.value.get(c.ruleId);
  const kind = c.kind ?? rule?.kind;
  const schedMovieId = c.schedMovieId ?? rule?.schedMovieId;
  const versionId = c.versionId ?? rule?.versionId;
  const version = versionId != null ? versionMap.value.get(`${schedMovieId}|${versionId}`) : null;
  // 目標回数（条件設定で入力したもの）の照合は source=TARGET で返り、説明文がない
  const isTarget = c.source === "TARGET";
  const hard = c.hard ?? rule?.hard;
  return {
    ...c,
    _key: `rc-${c.ruleId ?? i}`,
    hard,
    // 配給会社の必須外で判定不可（状態バーの「要確認」と同じ条件）
    pending: isDistributorUndeterminedCheck(c, hard),
    isCount: COUNT_KINDS.includes(kind),
    isShare: kind === "SHARE_PREF",
    perDay: (c.countUnit ?? rule?.countUnit) === "PER_DAY",
    status: RULE_CHECK_STATUS[c.status] || { label: c.status || "-", type: "info" },
    title: c.description || rule?.description || (isTarget ? "目標回数" : `ルール ${c.ruleId ?? ""}`),
    meta: [
      isTarget ? "" : optionLabel(RULE_SOURCE_OPTIONS, c.source ?? rule?.source),
      optionLabel(RULE_KIND_OPTIONS, kind),
      schedMovieId != null ? movieMap.value.get(schedMovieId)?.title : "全作品",
      version ? versionLabel(version) : "",
      c.timeFrom || c.timeTo ? `${c.timeFrom || ""}〜${c.timeTo || ""}` : "",
      // 配分指定はスクリーン指定もありうる（照合結果に screenId はないのでルール側から取る）
      kind === "SHARE_PREF" && rule?.screenId != null ? screenMap.value.get(rule.screenId)?.name : "",
    ].filter(Boolean).join("・"),
  };
}));
const unsatisfiedCount = computed(() => ruleChecks.value.filter((c) => c.status === RULE_CHECK_STATUS.UNSATISFIED).length);

const gapText = (c) => {
  if (c.gap == null) return "-";
  return c.gap > 0 ? `+${c.gap}` : String(c.gap);
};

const violationPlace = (v) => [
  v.bizDate ? shortDateLabel(v.bizDate) : "",
  v.screenId != null ? screenMap.value.get(v.screenId)?.name || "" : "",
].filter(Boolean).join(" ");

// ---------- AI 説明（summary が null なら未生成） ----------
const policyText = computed(() => summary.value?.policy || POLICY_TEXT);
const overallText = computed(() => summary.value?.overall || "");
const movieNotes = computed(() => (summary.value?.perMovie || [])
  .map((n, i) => ({
    key: `${n.schedMovieId ?? i}`,
    title: n.title || movieMap.value.get(n.schedMovieId)?.title || "",
    text: n.text || "",
  }))
  .filter((n) => n.text));
const dataSources = computed(() => summary.value?.dataSources || []);
// AI が報告した未達項目
const unmetNotes = computed(() => (summary.value?.unmet || []).map((u, i) => ({
  key: `${u.ruleId ?? i}`,
  title: ruleMap.value.get(u.ruleId)?.description
    || (check.value?.ruleChecks || []).find((c) => c.ruleId === u.ruleId)?.description
    || `ルール ${u.ruleId ?? ""}`,
  reason: u.reason || "",
})));
// サーバー側で調整した件数の注記
const adjustNotes = computed(() => {
  const s = summary.value;
  if (!s) return [];
  const notes = [];
  if (s.adjustedCount) notes.push(`重複・清掃間隔のため ${s.adjustedCount}回の開始時刻を後ろにずらしました`);
  if (s.droppedCount) notes.push(`上映できない・営業時間外のため AI案から ${s.droppedCount}回を除外しました`);
  if (s.editedAfterGeneration) notes.push("生成後に手動で編集・保存されています");
  return notes;
});
</script>

<template>
  <div class="check-panel" :class="`is-${section}`">
    <!-- ルール照合表 -->
    <section v-if="showCheck" class="panel">
      <div class="panel-head">
        <h3>ルール照合<span v-if="unsatisfiedCount" class="count-danger">未達 {{ unsatisfiedCount }}</span></h3>
        <span v-if="validating" class="panel-note">チェック中…</span>
      </div>
      <el-empty v-if="!ruleChecks.length" description="照合するルールはありません" :image-size="48" />
      <ul v-else class="check-list">
        <li v-for="c in ruleChecks" :key="c._key" class="check-item">
          <div class="check-item__head">
            <el-tag :type="c.status.type" size="small" effect="dark">{{ c.status.label }}</el-tag>
            <el-tag v-if="c.hard" type="danger" size="small" effect="plain">必須</el-tag>
            <el-tag v-else-if="c.pending" type="warning" size="small" effect="plain">要確認</el-tag>
            <span class="check-item__title">{{ c.title }}</span>
          </div>
          <div v-if="c.meta" class="check-item__meta">{{ c.meta }}</div>
          <dl v-if="c.isCount" class="count-grid">
            <div><dt>要求{{ c.perDay ? "/日" : "" }}</dt><dd>{{ c.required ?? "-" }}</dd></div>
            <div><dt>{{ c.perDay ? "計画（該当日）" : "計画" }}</dt><dd>{{ c.actual ?? "-" }}</dd></div>
            <div><dt>{{ c.perDay ? "差（合計）" : "差" }}</dt><dd :class="{ 'is-short': c.gap < 0 }">{{ gapText(c) }}</dd></div>
          </dl>
          <dl v-else-if="c.isShare" class="count-grid">
            <div><dt>目安</dt><dd>{{ c.required != null ? `${c.required}%` : "-" }}</dd></div>
            <div><dt>計画</dt><dd>{{ c.actual != null ? `${c.actual}%` : "-" }}</dd></div>
            <div><dt>不足</dt><dd :class="{ 'is-short': c.gap > 0 }">{{ c.gap > 0 ? `${c.gap}回` : "-" }}</dd></div>
          </dl>
          <p v-if="c.reason" class="check-item__reason">{{ c.reason }}</p>
        </li>
      </ul>
    </section>

    <!-- 違反一覧 -->
    <section v-if="showCheck" class="panel">
      <div class="panel-head">
        <h3>違反<span class="panel-count" :class="{ 'count-danger': violations.length }">{{ violations.length }}</span></h3>
        <span class="panel-note">クリックで該当の上映回を表示</span>
      </div>
      <p v-if="!violations.length" class="empty-text">違反はありません</p>
      <ul v-else class="violation-list">
        <li v-for="v in violations" :key="v._key">
          <button type="button" class="violation-item" @click="emit('select-violation', v)">
            <el-tag type="danger" size="small" effect="plain">{{ v.typeLabel }}</el-tag>
            <span class="violation-item__place">{{ violationPlace(v) }}</span>
            <span class="violation-item__msg">{{ v.message }}</span>
          </button>
        </li>
      </ul>
    </section>

    <!-- AI 説明 -->
    <section v-if="showSummary" class="panel">
      <div class="panel-head">
        <h3>AIの説明</h3>
        <span v-if="generatedTime" class="panel-note">生成 {{ generatedTime }}</span>
      </div>
      <div class="summary-body">
        <p class="policy">{{ policyText }}</p>
        <div class="seats">
          <span class="seats__label">予測動員数合計</span>
          <span class="seats__value">{{ estTotalSeats != null ? `${estTotalSeats.toLocaleString()}人` : "-" }}</span>
        </div>
        <p v-if="estUnknownCount" class="hint">
          予測できない上映回 {{ estUnknownCount }}回（公開前などデータがないため合計に含めていません）
        </p>
        <p v-if="showsDirty" class="hint">※ 予測動員は保存済みのタイムチャートの値です</p>

        <template v-if="summary">
          <ul v-if="adjustNotes.length" class="adjust-notes">
            <li v-for="n in adjustNotes" :key="n">{{ n }}</li>
          </ul>

          <template v-if="dataSources.length">
            <h4>データ根拠</h4>
            <el-table :data="dataSources" size="small" class="source-table">
              <el-table-column label="データ" min-width="120">
                <template #default="{ row }">
                  {{ row.name }}
                  <div v-if="row.note" class="source-note">{{ row.note }}</div>
                </template>
              </el-table-column>
              <el-table-column label="期間" min-width="100">
                <template #default="{ row }">{{ row.period || "-" }}</template>
              </el-table-column>
              <el-table-column label="地域" min-width="70">
                <template #default="{ row }">{{ row.region || "-" }}</template>
              </el-table-column>
            </el-table>
          </template>

          <template v-if="overallText">
            <h4>全体の説明</h4>
            <p class="summary-text">{{ overallText }}</p>
          </template>

          <template v-if="movieNotes.length">
            <h4>作品ごとの説明</h4>
            <dl class="movie-notes">
              <div v-for="n in movieNotes" :key="n.key">
                <dt>{{ n.title }}</dt>
                <dd class="summary-text">{{ n.text }}</dd>
              </div>
            </dl>
          </template>

          <template v-if="unmetNotes.length">
            <h4>満たせなかった条件</h4>
            <dl class="movie-notes unmet-notes">
              <div v-for="n in unmetNotes" :key="n.key">
                <dt>{{ n.title }}</dt>
                <dd class="summary-text">{{ n.reason }}</dd>
              </div>
            </dl>
          </template>

          <p v-if="summary.model" class="model-note">モデル：{{ summary.model }}</p>
        </template>
        <p v-else class="empty-text empty-text--inline">AI生成後に説明が表示されます</p>
      </div>
    </section>
  </div>
</template>

<style lang="less" scoped>
.check-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;

  h4 {
    margin: 14px 0 6px;
    font-size: 13px;
  }
}

.count-danger {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--el-color-danger);
}

.empty-text {
  margin: 0;
  padding: 14px 18px;
  font-size: 13px;
  color: var(--sp-muted);
}

/* ---------- ルール照合 ---------- */
.check-list,
.violation-list {
  max-height: 360px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.check-item {
  padding: 10px 18px;

  & + & {
    border-top: 1px solid var(--sp-border);
  }

  &__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
  }

  &__meta,
  &__reason {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--sp-muted);
  }
}

.count-grid {
  display: flex;
  gap: 16px;
  margin: 6px 0 0;

  div {
    display: flex;
    gap: 4px;
    align-items: baseline;
  }

  dt {
    font-size: 12px;
    color: var(--sp-muted);
  }

  dd {
    margin: 0;
    font-weight: 700;
    font-variant-numeric: tabular-nums;

    &.is-short {
      color: var(--el-color-danger);
    }
  }
}

/* ---------- 違反 ---------- */
.violation-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  width: 100%;
  padding: 8px 18px;
  border: none;
  border-bottom: 1px solid var(--sp-border);
  font: inherit;
  font-size: 12px;
  text-align: left;
  color: var(--sp-text);
  background: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    outline: none;
    background: var(--sp-tint);
  }

  &__place {
    font-weight: 600;
  }

  &__msg {
    flex-basis: 100%;
    color: var(--sp-text-2);
  }
}

/* ---------- AI 説明 ---------- */
.summary-body {
  padding: 12px 18px 16px;
}

// タイムチャートの下に全幅で置くときは、予測動員を左に寄せて横長の文章を読みやすくする
.is-summary {
  .seats {
    justify-content: flex-start;
    gap: 16px;
  }

  .summary-text {
    max-width: 960px;
  }
}

.policy {
  margin: 0 0 10px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 12px;
  background: var(--sp-tint);
}

.seats {
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  &__label {
    font-size: 13px;
    color: var(--sp-text-2);
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.summary-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
}

.adjust-notes {
  margin: 10px 0 0;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--sp-text-2);
}

.source-note {
  font-size: 11px;
  color: var(--sp-muted);
}

.unmet-notes dt {
  color: var(--el-color-danger);
}

.model-note {
  margin: 14px 0 0;
  font-size: 11px;
  color: var(--sp-muted);
}

.empty-text--inline {
  padding: 10px 0 0;
}

.movie-notes {
  margin: 0;

  div + div {
    margin-top: 8px;
  }

  dt {
    font-size: 13px;
    font-weight: 600;
  }

  dd {
    margin: 2px 0 0;
  }
}
</style>
